// ─────────────────────────────────────────────
// Enums
// ─────────────────────────────────────────────

export enum UserPresence {
  ONLINE = 'online',
  AWAY = 'away',
  BUSY = 'busy',
  OFFLINE = 'offline',
}

export enum MessageContentType {
  TEXT = 'text',
  MARKDOWN = 'markdown',
  CODE = 'code',
  SYSTEM = 'system',
  HTML = 'html',
}

export enum ConversationType {
  DIRECT = 'direct',
  GROUP = 'group',
}

export enum RoleName {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  TEAM_OWNER = 'team_owner',
  MEMBER = 'member',
  GUEST = 'guest',
}

export enum NotificationType {
  MESSAGE = 'message',
  MENTION = 'mention',
  REACTION = 'reaction',
  TEAM_INVITE = 'team_invite',
  CHANNEL_INVITE = 'channel_invite',
  SYSTEM = 'system',
}

export enum AuditAction {
  USER_LOGIN = 'user.login',
  USER_LOGOUT = 'user.logout',
  USER_CREATED = 'user.created',
  USER_UPDATED = 'user.updated',
  USER_DELETED = 'user.deleted',
  ROLE_ASSIGNED = 'role.assigned',
  MESSAGE_DELETED = 'message.deleted',
  CHANNEL_CREATED = 'channel.created',
  CHANNEL_ARCHIVED = 'channel.archived',
  TEAM_CREATED = 'team.created',
  FILE_UPLOADED = 'file.uploaded',
}

// ─────────────────────────────────────────────
// Socket.IO Event Names
// ─────────────────────────────────────────────

export const SocketEvents = {
  // Message
  MESSAGE_SEND: 'message:send',
  MESSAGE_NEW: 'message:new',
  MESSAGE_EDIT: 'message:edit',
  MESSAGE_EDITED: 'message:edited',
  MESSAGE_DELETE: 'message:delete',
  MESSAGE_DELETED: 'message:deleted',
  MESSAGE_UNDO_DELETE: 'message:undo_delete',
  MESSAGE_RESTORED: 'message:restored',
  MESSAGE_ACK: 'message:ack',

  // Typing
  TYPING_START: 'typing:start',
  TYPING_STOP: 'typing:stop',
  TYPING_UPDATE: 'typing:update',

  // Presence
  PRESENCE_UPDATE: 'presence:update',
  PRESENCE_BULK: 'presence:bulk',

  // Reaction
  REACTION_ADD: 'reaction:add',
  REACTION_REMOVE: 'reaction:remove',
  REACTION_UPDATED: 'reaction:updated',

  // Notification
  NOTIFICATION_NEW: 'notification:new',

  // Room
  ROOM_JOIN: 'room:join',
  ROOM_LEAVE: 'room:leave',

  // Users
  USER_CREATED: 'user:created',
  USER_UPDATED: 'user:updated',
  USER_DELETED: 'user:deleted',
  USER_ACTIVATED: 'user:activated',
  USER_DEACTIVATED: 'user:deactivated',

  // Attention (Spark-style buzz)
  ATTENTION_SEND: 'attention:send',
  ATTENTION_RECEIVED: 'attention:received',
} as const;

// ─────────────────────────────────────────────
// Shared DTOs
// ─────────────────────────────────────────────

export type WorkLocation = 'office' | 'remote' | null;

export interface UserDto {
  id: string;
  email: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  presence: UserPresence;
  lastSeen: string;
  isActive: boolean;
  roles?: string[];
  department?: string;
  workLocation?: WorkLocation;
}

export interface MessageDto {
  id: string;
  channelId?: string;
  conversationId?: string;
  senderId: string;
  sender: Pick<UserDto, 'id' | 'displayName' | 'avatarUrl'>;
  parentId?: string;
  parent?: {
    id: string;
    content: string;
    contentType: string;
    isDeleted: boolean;
    sender: Pick<UserDto, 'id' | 'displayName'>;
    attachments?: AttachmentDto[];
  } | null;
  content: string;
  contentType: MessageContentType;
  metadata: Record<string, unknown>;
  isEdited: boolean;
  isDeleted: boolean;
  reactions: ReactionDto[];
  attachments: AttachmentDto[];
  createdAt: string;
  updatedAt: string;
}

export interface ReactionDto {
  id: string;
  emoji: string;
  userId: string;
  user: Pick<UserDto, 'id' | 'displayName'>;
}

export interface AttachmentDto {
  id: string;
  filename: string;
  mimeType: string;
  fileSize: number;
  storageUrl: string;
}

export interface ChannelDto {
  id: string;
  teamId: string;
  name: string;
  description: string | null;
  isPrivate: boolean;
  isArchived: boolean;
  memberCount: number;
  createdAt: string;
}

export interface TeamDto {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  avatarUrl: string | null;
  isPrivate: boolean;
  memberCount: number;
  channels: ChannelDto[];
  members?: any[];
  createdAt: string;
}

export interface NotificationDto {
  id: string;
  type: NotificationType;
  payload: Record<string, unknown>;
  isRead: boolean;
  createdAt: string;
}

// ─────────────────────────────────────────────
// Socket Payload Types
// ─────────────────────────────────────────────

export interface SendMessagePayload {
  channelId?: string;
  conversationId?: string;
  content: string;
  contentType: MessageContentType;
  parentId?: string;
  metadata?: Record<string, unknown>;
}

export interface TypingPayload {
  channelId?: string;
  conversationId?: string;
  userId: string;
  displayName: string;
}

export interface PresencePayload {
  userId: string;
  presence: UserPresence;
}
